import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';
import { mix } from 'polished';
import { useState } from 'react';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Avatar, Button, IconButton, Input, Paragraph, Spinner } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { Trans, useTranslation } from 'react-i18next';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import useViewer from 'packages/kakapo-ui-application/hooks/useViewer';
import EngagementQuestionButton from './EngagementQuestionButton';

const Score = styled(Box)<{ value?: number }>`
	background: ${(props) =>
		typeof props.value === 'number'
			? props.value >= 0.5
				? mix(props.value * 2 - 1, props.theme.colors.green, props.theme.colors.white)
				: mix(props.value * 2, props.theme.colors.white, props.theme.colors.red)
			: props.theme.colors.lightBlueLight};
	width: 26px;
	height: 26px;
	flex-grow: 0;
	flex-shrink: 0;
	margin-right: 4px;
	border-radius: 13px;
	font-size: 12px;
	font-weight: bold;
	text-align: center;
	line-height: 26px;
`;

const QuestionContainer = styled(Flex)<{ first?: boolean }>`
	padding: 10px 12px;

	${(props) =>
		!props.first &&
		`
		border-top: 1px solid ${props.theme.colors.lightBlue};
	`}
`;

const Delta = styled(Flex)<{ up: boolean }>`
	color: ${(props) => (props.up ? props.theme.colors.green : props.theme.colors.red)};

	svg {
		transform: rotate(${(props) => (props.up ? -90 : 90)}deg);

		path {
			fill: ${(props) => (props.up ? props.theme.colors.green : props.theme.colors.red)};
		}
	}
`;

const Arrow = styled(Box)`
	svg {
		display: block;
		width: 24px;
		height: 24px;
	}
`;

function EngagementQuestionScore(props: {
	question: graphql.AnalyticsEngagementQuestionScoreTrend;
}) {
	const score = props.question.datapoints[props.question.datapoints.length - 1]?.score;

	return (
		<>
			<Score value={typeof score === 'number' ? (score - 1) / 9 : undefined}>
				{score?.toFixed(1) ?? '-'}
			</Score>
			{typeof props.question.delta !== 'number' ? (
				<Box width={56} flexGrow={0} flexShrink={0} />
			) : (
				<Delta
					mx={2}
					width={40}
					alignItems="center"
					flexGrow={0}
					flexShrink={0}
					up={props.question.delta >= 0}
				>
					<Box flexGrow={0} flexShrink={0}>
						{Math.abs(props.question.delta).toFixed(1)}
					</Box>
					<Arrow>
						<ChevronRightIcon />
					</Arrow>
				</Delta>
			)}
		</>
	);
}

const EngagementQuestionAnswerContent = styled(Box)`
	border-radius: 5px;
	background: ${({ theme }) => theme.colors.lightBlueLight};
	overflow-wrap: break-word;
	hyphens: auto;
`;

function EngagementQuestionAnswer({
	answer,
}: {
	answer: graphql.AnalyticsEngagementOpenQuestionAnswer;
}) {
	const useEngagementReply = useFeature(graphql.FeatureName.EngagementReply);

	const viewer = useViewer();
	const userRoles = useViewerRoles();
	const isSuper = userRoles.isWorkspaceSuperuser || userRoles.isEngagementOfficer;

	const managerID = isSuper ? null : viewer!.id;

	const { data } = graphql.useEngagementAnswerWithRepliesQuery({
		variables: {
			id: answer.id,
			managerID,
		},
	});

	return (
		<Flex mt={2} alignItems="center">
			<EngagementQuestionAnswerContent p={3} flexGrow={1}>
				{answer.text}
			</EngagementQuestionAnswerContent>

			{useEngagementReply && (
				<EngagementQuestionButton
					answerID={answer.id}
					repliesCount={data?.openQuestionAnswerWithReplies.replies.length}
				/>
			)}
		</Flex>
	);
}

function EngagementQuestionAnswers(props: {
	answers: graphql.AnalyticsEngagementOpenQuestionAnswer[];
}) {
	return (
		<Box mt={3}>
			{props.answers.map((answer) => (
				<EngagementQuestionAnswer key={answer.id} answer={answer} />
			))}
		</Box>
	);
}

export default function EngagementQuestion(props: {
	question: graphql.AnalyticsEngagementQuestionScoreTrend | graphql.AnalyticsEngagementOpenQuestion;
	first?: boolean;
}) {
	const { localize } = useLocalize();
	const [isToggled, setIsToggled] = useState(false);

	return (
		<QuestionContainer alignItems="center" first={props.first}>
			{'datapoints' in props.question ? (
				<EngagementQuestionScore question={props.question} />
			) : (
				<Box flexGrow={0} flexShrink={0} />
			)}
			{props.question.__typename === 'AnalyticsEngagementOpenQuestion' ? (
				<>
					<IconButton
						type="plain"
						icon={<StyledChevronIcon $opened={isToggled} />}
						onClick={() => setIsToggled(!isToggled)}
						mr={2}
						alignSelf="start"
						flexShrink={0}
					/>
					<Box py="5px" flexGrow={1}>
						<Paragraph color="slateBlue">{localize(props.question.question.title).text}</Paragraph>

						{isToggled && 'answers' in props.question && (
							<EngagementQuestionAnswers answers={props.question.answers} />
						)}
					</Box>
				</>
			) : (
				<Box py="5px">
					<Paragraph color="slateBlue">{localize(props.question.question.title).text}</Paragraph>
				</Box>
			)}
		</QuestionContainer>
	);
}

const StyledChevronIcon = styled(ChevronRightIcon as any)<{ $opened: boolean }>`
	${({ $opened }) => css`
		transform: rotate(${$opened ? 90 : 0}deg);
	`}
`;
